import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Bell } from '@factorialco/f0-react/icons/app'
import {
  BulkNotifyPostsPostDocument,
  GetPostMemberStatusesDocument,
  type GetPostMemberStatusesQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type MemberStatusNode =
  GetPostMemberStatusesQuery['posts']['postMemberStatusesConnection']['edges'][number]['node']

type Props = {
  postId: string
  hasRequiredActions: boolean
}

const RESEND_NOTIFICATION_ACTION = 'resend-notification'

const AudienceCollection = ({ postId, hasRequiredActions }: Props) => {
  const [bulkNotify] = useMutation(BulkNotifyPostsPostDocument, {
    extractorFn: (data) => data.postsMutations.bulkNotifyPostsPost,
  })

  const MEMBER_STATUSES = {
    NOT_SEEN: {
      label: i18n.t('posts.communities.insights.member_status.not_seen'),
      color: 'lilac',
    },
    SEEN: { label: i18n.t('posts.communities.insights.member_status.seen'), color: 'purple' },
    OPENED: { label: i18n.t('posts.communities.insights.member_status.opened'), color: 'viridian' },
    COMPLETED: {
      label: i18n.t('posts.communities.insights.member_status.completed'),
      color: 'grass',
    },
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: GetPostMemberStatusesDocument,
      mapVariables: ({ filters }) => {
        const statusValues = Array.isArray(filters.status) ? filters.status : []
        return {
          postId,
          seen: statusValues.includes('notSeen') ? false : undefined,
          opened: statusValues.includes('notOpened') ? false : undefined,
          completed:
            hasRequiredActions && statusValues.includes('notCompleted') ? false : undefined,
        }
      },
      mapData: (data: GetPostMemberStatusesQuery) =>
        data?.posts?.postMemberStatusesConnection?.edges?.map((edge) => edge.node) ?? [],
      mapPagination: (data: GetPostMemberStatusesQuery) => ({
        total: data?.posts?.postMemberStatusesConnection?.totalCount ?? 0,
      }),
      paginationType: 'pages',
      selectable: (item: MemberStatusNode) => item.id,
      allPagesSelection: true,
      bulkActions: () => ({
        primary: [
          {
            id: RESEND_NOTIFICATION_ACTION,
            label: i18n.t('posts.communities.insights.resend_notification'),
            icon: Bell,
          },
        ],
      }),
      filters: {
        status: {
          type: 'in' as const,
          label: i18n.t('posts.communities.insights.status'),
          options: {
            options: [
              {
                value: 'notSeen',
                label: i18n.t('posts.communities.insights.member_status.not_seen'),
              },
              {
                value: 'notOpened',
                label: i18n.t('posts.communities.insights.member_status.not_opened'),
              },
              ...(hasRequiredActions
                ? [
                    {
                      value: 'notCompleted',
                      label: i18n.t('posts.communities.insights.member_status.not_completed'),
                    },
                  ]
                : []),
            ],
          },
        },
      },
      presets: [
        {
          label: i18n.t('posts.communities.insights.member_status.not_seen'),
          filter: { status: ['notSeen'] },
        },
        {
          label: i18n.t('posts.communities.insights.member_status.not_opened'),
          filter: { status: ['notOpened'] },
        },
        ...(hasRequiredActions
          ? [
              {
                label: i18n.t('posts.communities.insights.member_status.not_completed'),
                filter: { status: ['notCompleted'] },
              },
            ]
          : []),
      ],
    },
    [hasRequiredActions]
  )

  const handleBulkAction = async (
    action: string,
    {
      allSelected,
      itemsStatus,
      filters,
    }: {
      allSelected: boolean | 'indeterminate'
      itemsStatus: readonly { item: MemberStatusNode; checked: boolean }[]
      selectedIds: readonly (string | number)[]
      filters: Record<string, unknown>
    },
    clearSelectedItems: () => void
  ) => {
    if (action !== RESEND_NOTIFICATION_ACTION) return

    let result

    if (allSelected === false) {
      const employeeIds = itemsStatus
        .filter((entry) => entry.checked)
        .map((entry) => entry.item.employeeId)
        .filter(Boolean)

      if (employeeIds.length === 0) return

      result = await bulkNotify({ variables: { id: postId, employeeIds } })
    } else {
      const excludedEmployeeIds =
        allSelected === 'indeterminate'
          ? itemsStatus
              .filter((entry) => !entry.checked)
              .map((entry) => entry.item.employeeId)
              .filter(Boolean)
          : []

      const statusValues = Array.isArray(filters.status) ? filters.status : []

      result = await bulkNotify({
        variables: {
          id: postId,
          selectAll: true,
          excludedEmployeeIds,
          seen: statusValues.includes('notSeen') ? false : undefined,
          opened: statusValues.includes('notOpened') ? false : undefined,
          completed:
            hasRequiredActions && statusValues.includes('notCompleted') ? false : undefined,
        },
      })
    }

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('posts.communities.insights.resend_notification_success'),
      })
    }

    clearSelectedItems()
  }

  return (
    <OneDataCollection
      source={source}
      onBulkAction={handleBulkAction}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                id: 'name',
                label: i18n.t('posts.communities.insights.name'),
                width: 200,
                render: (item: MemberStatusNode) => ({
                  type: 'person' as const,
                  value: {
                    firstName: item.employeeName?.firstName ?? '',
                    lastName: item.employeeName?.lastName ?? '',
                  },
                }),
              },
              {
                id: 'status',
                label: i18n.t('posts.communities.insights.status'),
                render: (item: MemberStatusNode) => ({
                  type: 'dotTag',
                  value:
                    hasRequiredActions && item.completed
                      ? MEMBER_STATUSES.COMPLETED
                      : item.opened
                        ? MEMBER_STATUSES.OPENED
                        : item.seen
                          ? MEMBER_STATUSES.SEEN
                          : MEMBER_STATUSES.NOT_SEEN,
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}

export default AudienceCollection
